import dayjs from 'dayjs'

export const PURCHASE_TYPES = [
  { value: 'collection', label: 'Collection' },
  { value: 'bundle', label: 'Bundle' },
  { value: 'monthly', label: 'Monthly subscription' },
  { value: 'annual', label: 'Annual subscription' }
]

export const AFFILIATE_STATUSES = [
  { value: 'invited', label: 'Invited' },
  { value: 'submitted', label: 'Submitted' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'suspended', label: 'Suspended' },
  { value: 'closed', label: 'Closed' }
]

export const PAYOUT_BATCH_STATUSES = [
  { value: 'prepared', label: 'Prepared' },
  { value: 'approved', label: 'Approved' },
  { value: 'dispatching', label: 'Dispatching' },
  { value: 'transferred', label: 'Transferred' },
  { value: 'recording', label: 'Recording' },
  { value: 'paid', label: 'Paid' },
  { value: 'canceled', label: 'Canceled' }
]

export const OBJECT_ID_PATTERN = /^[a-f0-9]{24}$/i

const CURRENCY_SYMBOLS = {
  usd: '$',
  sgd: 'S$',
  aud: 'A$',
  cad: 'C$',
  hkd: 'HK$',
  nzd: 'NZ$',
  eur: '€',
  gbp: '£'
}

const REASON_MAX_LENGTH = 500

function withSign(negative, text) {
  return negative ? `-${text}` : text
}

function toFixedMinor(amount) {
  const value = Math.abs(Number(amount)) / 100
  return value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

function isMissing(value) {
  return value === null || typeof value === 'undefined' || value === ''
}

/** USD cents → "$12.34". */
export function formatUsdCents(cents) {
  if (isMissing(cents) || Number.isNaN(Number(cents))) return '-'
  return withSign(Number(cents) < 0, `$${toFixedMinor(cents)}`)
}

/** Settlement-currency minor units → "S$12.34" (falls back to "XYZ 12.34"). */
export function formatMinorUnits(amount, currency) {
  if (isMissing(amount) || Number.isNaN(Number(amount))) return '-'
  const code = String(currency || '').toLowerCase()
  const symbol = CURRENCY_SYMBOLS[code]
  const body = toFixedMinor(amount)
  const text = symbol ? `${symbol}${body}` : `${code ? code.toUpperCase() + ' ' : ''}${body}`
  return withSign(Number(amount) < 0, text)
}

/** Basis points → "12.5%". */
export function formatBps(bps) {
  if (isMissing(bps) || Number.isNaN(Number(bps))) return '-'
  const percent = Number(bps) / 100
  return `${Number(percent.toFixed(2))}%`
}

/** Unix seconds → local "YYYY-MM-DD HH:mm". */
export function formatTime(ts, withSeconds = false) {
  const value = Number(ts || 0)
  if (!value) return '-'
  return dayjs.unix(value).format(withSeconds ? 'YYYY-MM-DD HH:mm:ss' : 'YYYY-MM-DD HH:mm')
}

export function idOf(value) {
  if (!value) return ''
  if (typeof value === 'string') return value
  if (typeof value === 'object') {
    if (value._id !== undefined) return idOf(value._id)
    if (typeof value.id === 'string') return value.id
    return value.$oid || value.$id || ''
  }
  return String(value)
}

export function shortId(value, length = 10) {
  const id = idOf(value)
  return id.length > length + 2 ? `${id.slice(0, length)}…` : id
}

export function labelFor(options, value) {
  const match = options.find((option) => option.value === value)
  return match ? match.label : (value || '-')
}

export default {
  data() {
    return {
      pending: {}
    }
  },
  computed: {
    anyPending() {
      return Object.keys(this.pending).length > 0
    }
  },
  methods: {
    formatUsdCents,
    formatMinorUnits,
    formatBps,
    formatTime,
    idOf,
    shortId,
    isPending(key) {
      return !!this.pending[key]
    },
    responseMessage(body, fallback) {
      const msg = body && typeof body.msg === 'string' ? body.msg.trim() : ''
      return msg || fallback
    },
    /** Read-only request. Returns `data` or `undefined` after showing the backend message. */
    async loadData(requestFn, fallback) {
      try {
        const res = await requestFn()
        const body = res && res.data
        if (body && Number(body.code) === 0) return body.data
        if (body && Number(body.code) === -401) return undefined
        this.$message.error(this.responseMessage(body, fallback))
      } catch (error) {
        this.$message.error(fallback)
      }
      return undefined
    },
    /**
     * Runs one mutation while its button is disabled.
     * Resolves to `{ data }` on success, or null after showing the backend `msg`.
     */
    async runMutation(key, requestFn, { success = '', failure = 'Request failed' } = {}) {
      if (this.pending[key]) return null
      this.$set(this.pending, key, true)
      try {
        const res = await requestFn()
        const body = res && res.data
        if (body && Number(body.code) === 0) {
          if (success) this.$message.success(success)
          return { data: body.data, msg: body.msg }
        }
        if (!body || Number(body.code) !== -401) this.$message.error(this.responseMessage(body, failure))
      } catch (error) {
        this.$message.error(failure)
      } finally {
        this.$delete(this.pending, key)
      }
      return null
    },
    /** Lines may be strings (rendered as paragraphs) or VNodes (rendered as-is). */
    confirmMessage(lines) {
      const h = this.$createElement
      const list = Array.isArray(lines) ? lines : [lines]
      return h('div', { class: 'affiliate-confirm-message' }, list.filter(Boolean).map((line) => (
        typeof line === 'string' ? h('p', { style: 'margin: 0 0 6px; word-break: break-word;' }, line) : line
      )))
    },
    codeBlock(text) {
      return this.$createElement('pre', {
        style: 'margin: 6px 0 8px; padding: 8px 10px; max-height: 280px; overflow: auto; background: #f5f7fa; border: 1px solid #ebeef5; font-family: Menlo, Consolas, monospace; font-size: 12px; line-height: 1.45; white-space: pre-wrap; word-break: break-all;'
      }, text)
    },
    /** Plain confirmation. Resolves true when the admin confirms. */
    async confirmAction({ title, lines, confirmText = 'Confirm', type = 'warning', wide = false }) {
      try {
        await this.$confirm(this.confirmMessage(lines), title, {
          confirmButtonText: confirmText,
          cancelButtonText: 'Cancel',
          type,
          customClass: wide ? 'affiliate-wide-message-box' : '',
          closeOnClickModal: false
        })
        return true
      } catch (error) {
        return false
      }
    },
    /** Confirmation that also collects a required reason. Resolves the trimmed reason or null. */
    async confirmWithReason({ title, lines, confirmText = 'Confirm', type = 'warning', placeholder = 'Required: why is this change being made?', wide = false }) {
      try {
        const { value } = await this.$prompt(this.confirmMessage(lines), title, {
          confirmButtonText: confirmText,
          cancelButtonText: 'Cancel',
          type,
          customClass: wide ? 'affiliate-wide-message-box' : '',
          inputType: 'textarea',
          inputPlaceholder: placeholder,
          closeOnClickModal: false,
          inputValidator: (input) => {
            const text = String(input || '').trim()
            if (!text) return 'A reason is required.'
            if (text.length > REASON_MAX_LENGTH) return `Keep the reason under ${REASON_MAX_LENGTH} characters.`
            return true
          }
        })
        return String(value || '').trim()
      } catch (error) {
        return null
      }
    },
    async copyText(text, label = 'Copied') {
      if (!text) return
      try {
        if (navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(text)
        } else {
          const textarea = document.createElement('textarea')
          textarea.value = text
          textarea.style.position = 'fixed'
          textarea.style.opacity = '0'
          document.body.appendChild(textarea)
          try {
            textarea.select()
            if (!document.execCommand('copy')) throw new Error('Copy failed')
          } finally {
            document.body.removeChild(textarea)
          }
        }
        this.$message.success(label)
      } catch (error) {
        this.$message.error('Copy failed; select the text and copy it manually')
      }
    }
  }
}
